import { asArray, asBool, asNumber, asRecord, asString } from "@/data/mapper/common/mapper-utils";
import { TreatmentSummaryMapper } from "@/data/mapper/treatment/treatment-summary-mapper";
import {
  DashboardCard,
  DashboardData,
  DashboardMetric,
  DashboardRow,
} from "@/domain/entity/dashboard/dashboard-entity";
import { Plan } from "@/domain/entity/prescription/prescription-info-entity";

export class DashboardInfoMapper {
  static toEntity(dto: unknown): DashboardData {
    const map = unwrapDashboardRecord(dto);

    const cards = mapCards(map);

    const rowList = firstArray(
      map.prescriptionList,
      map.patients,
      map.rows,
      map.dashboardRows,
      map.patientList,
      map.dataList,
      map.items,
      map.list,
    );
    const rows = rowList
      ? rowList.map((item) => DashboardInfoMapper.toRowEntity(item))
      : [];

    return { cards, rows };
  }

  static toRowEntity(dto: unknown): DashboardRow {
    const map = asRecord(dto);
    const birthDate = readPatientBirthDate(map);
    const patientId = asString(map.patientId, "-");
    return {
      doctorName:
        map.doctorName == null && map.medicalExpertName == null
          ? null
          : asString(map.doctorName ?? map.medicalExpertName),
      userName: asString(map.userName ?? map.patientName ?? map.name ?? map.displayName, "-"),
      patientId,
      screeningId: asString(
        map.screeningId ?? map.screeningID ?? map.screening_id ?? map.patientScreeningId ?? patientId,
        "-",
      ),
      prescriptionNumber: asString(
        map.prescriptionNumber ?? map.prescriptionNo ?? map.prescriptionId ?? map.treatmentId,
        "-",
      ),
      status: asString(
        map.status ?? map.prescriptionStatus ?? map.treatmentStatus ?? map.treatmentState,
        "-",
      ),
      progressPercentage: toPercentValue(map.progressPercentage ?? map.complianceRate),
      prescriptionType: asString(map.prescriptionType ?? map.type ?? map.treatmentType, "-"),
      patientUuid: asString(map.patientUuid ?? map.userUuid),
      prescriptionUuid: asString(map.prescriptionUuid ?? map.treatmentUuid),
      phone:
        map.phone == null && map.patientPhone == null
          ? null
          : asString(map.phone ?? map.patientPhone),
      gender:
        map.gender == null && map.patientGender == null
          ? null
          : asString(map.gender ?? map.patientGender),
      birthDate,
      isAccountSetup:
        map.isAccountSetup == null && map.accountSetup == null
          ? null
          : asBool(map.isAccountSetup ?? map.accountSetup),
      isDeactivated: asBool(map.isDeactivated ?? map.deactivated ?? map.isDeleted),
      canAddTreatment: typeof map.canAddTreatment === "boolean" ? map.canAddTreatment : null,
      startDate:
        map.startDate == null && map.treatmentStartDate == null && map.prescriptionStartDate == null
          ? null
          : asString(map.startDate ?? map.treatmentStartDate ?? map.prescriptionStartDate),
      endDate:
        map.endDate == null && map.treatmentEndDate == null && map.prescriptionEndDate == null
          ? null
          : asString(map.endDate ?? map.treatmentEndDate ?? map.prescriptionEndDate),
      progressRate:
        map.progressRate == null ? null : toPercentValue(map.progressRate),
      performanceRate:
        map.performanceRate == null ? null : toPercentValue(map.performanceRate),
      plans: dashboardPlansFromMap(map),
      treatments: dashboardTreatmentsFromMap(map),
    };
  }
}

function readPatientBirthDate(map: Record<string, unknown>): string | null {
  const patient = asRecord(map.patient);
  const user = asRecord(map.user);
  const detail = asRecord(map.detail);
  const value =
    map.birthDate ??
    map.birthday ??
    map.patientBirthday ??
    map.dateOfBirth ??
    map.dob ??
    patient.birthDate ??
    patient.birthday ??
    patient.patientBirthday ??
    user.birthDate ??
    user.birthday ??
    user.patientBirthday ??
    detail.birthDate ??
    detail.birthday ??
    detail.patientBirthday;
  return value == null ? null : asString(value);
}

/** 서버 지표 키 → 대시보드 지표 종류 */
const metricMapping: Record<string, DashboardMetric> = {
  totalPatients: "totalPatients",
  totalPatientCount: "totalPatients",
  runningPatients: "inProgressPatients",
  inProgressPatients: "inProgressPatients",
  inProgressPatientCount: "inProgressPatients",
  patientsInProgress: "inProgressPatients",
  LessThan60RateCompliance: "lowCompliance",
  lessThan60RateCompliance: "lowCompliance",
  lowCompliance: "lowCompliance",
  lowCompliancePatients: "lowCompliance",
  lowCompliancePatientCount: "lowCompliance",
};

/**
 * 지표 키가 없는 응답에 대비해 서버가 내려준 제목으로도 지표를 판별한다.
 * (서버 제목은 한국어로 고정되어 있다.)
 */
function metricFromCard(key: string, title: string): DashboardMetric {
  const mapped = metricMapping[key];
  if (mapped) return mapped;
  if (title.includes("순응도")) return "lowCompliance";
  if (title.includes("진행")) return "inProgressPatients";
  if (title.includes("전체 환자")) return "totalPatients";
  return "unknown";
}

function unwrapDashboardRecord(dto: unknown): Record<string, unknown> {
  const map = asRecord(dto);
  if (hasDashboardShape(map)) return map;

  for (const key of ["data", "result", "content"]) {
    const nested = asRecord(map[key]);
    if (hasDashboardShape(nested)) return nested;
  }

  return map;
}

function hasDashboardShape(map: Record<string, unknown>): boolean {
  return Boolean(
    map.cardsData ??
      map.statistics ??
      map.cards ??
      map.prescriptionList ??
      map.patients ??
      map.rows ??
      map.dashboardRows ??
      map.patientList ??
      map.dataList,
  );
}

function mapCards(map: Record<string, unknown>): DashboardCard[] {
  const cards = firstArray(map.cards, map.cardList, map.cardsList);
  if (cards) {
    return cards.map(toCardEntity);
  }

  const cardsMap = asRecord(map.cardsData ?? map.statistics);
  return Object.entries(cardsMap).map(([key, value]) => {
    const item = asRecord(value);
    return toCardEntity({ ...item, key });
  });
}

function toCardEntity(dto: unknown): DashboardCard {
  const map = asRecord(dto);
  const key = asString(map.key ?? map.type ?? map.name);
  const title = asString(map.title ?? map.label ?? key, "-");
  return {
    metric: metricFromCard(key, title),
    title,
    value: asNumber(map.value ?? map.count ?? map.total),
    percentageChange: nullableNumber(
      map.percentageChange ?? map.changeFromLastMonth ?? map.changeRate ?? map.change,
    ),
  };
}

function nullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  return asNumber(value);
}

function toPercentValue(value: unknown): number {
  const numericValue = asNumber(value);
  const percentValue = numericValue <= 1 ? numericValue * 100 : numericValue;
  return Number(percentValue.toFixed(2));
}

function dashboardPlansFromMap(map: Record<string, unknown>): Plan[] {
  const rawPlans = firstArray(map.plans, map.planList, map.activities, map.activityList);
  if (!rawPlans) return [];
  return rawPlans.map(toDashboardPlan);
}

function dashboardTreatmentsFromMap(map: Record<string, unknown>) {
  const rawTreatments = firstArray(map.treatments, map.treatmentList);
  if (!rawTreatments) return [];
  return rawTreatments.map((item) => TreatmentSummaryMapper.toEntity(item));
}

function toDashboardPlan(dto: unknown): Plan {
  const map = asRecord(dto);
  const isCompleted = map.isCompleted ?? map.status;
  const rawStatus =
    typeof isCompleted === "boolean" ? (isCompleted ? "완료" : "미완료") : isCompleted;

  return {
    planUuid: asString(map.planUuid ?? map.uuid ?? map.activityUuid, ""),
    title: asString(map.title ?? map.activityName ?? map.name, ""),
    day: map.day == null && map.aFewDays == null ? undefined : asNumber(map.day ?? map.aFewDays),
    status: rawStatus == null ? undefined : asString(rawStatus),
    activityCount: map.activityCount == null ? undefined : asNumber(map.activityCount),
    type: map.type == null ? null : asString(map.type),
    name: map.name == null ? null : asString(map.name),
    startAt: map.startAt == null && map.startTime == null ? null : asString(map.startAt ?? map.startTime),
    endAt: map.endAt == null && map.endTime == null ? null : asString(map.endAt ?? map.endTime),
    totalTime: map.totalTime == null ? null : asNumber(map.totalTime),
  };
}

function firstArray(...values: unknown[]): unknown[] | null {
  for (const value of values) {
    const array = asArray(value);
    if (array.length > 0) return array;
  }
  return null;
}
